/**
 * TITAM AI - Google Ads - Página de Valores e Custos
 * Custos por métrica: CPM, CPC, CPV, CPW, CPF, CPO, CPL, CPLQ, CAC
 */

import { useState, useMemo } from 'react'
import { DataTable } from '../../components/DataTable.jsx'
import { MetricFunnel } from '../../components/Metrics.jsx'

// Função para calcular todos os custos com dados reais
function enrichWithCosts(data) {
  return data.map(item => ({
    ...item,
    // Custos por métrica (Verificando divisão por zero)
    cpm: item.impressions > 0 ? (item.cost || 0) / item.impressions * 1000 : 0,
    cpc: item.clicks > 0 ? (item.cost || 0) / item.clicks : 0,
    cpv: item.visits > 0 ? (item.cost || 0) / item.visits : 0,
    cpw: item.whatsappClicks > 0 ? (item.cost || 0) / item.whatsappClicks : 0,
    cpf: item.forms > 0 ? (item.cost || 0) / item.forms : 0,
    cpo: item.contacts > 0 ? (item.cost || 0) / item.contacts : 0,
    cpl: item.leads > 0 ? (item.cost || 0) / item.leads : 0,
    cplq: item.qualifiedLeads > 0 ? (item.cost || 0) / item.qualifiedLeads : 0,
    cac: item.sales > 0 ? (item.cost || 0) / item.sales : 0,
  }))
}

// Colunas para cada nível
const ACCOUNT_COLUMNS = [
  { key: 'name', label: 'Conta', sortable: true },
  { key: 'cost', label: 'Investimento', format: 'currency', sortable: true, align: 'right' },
  { key: 'revenue', label: 'Receita', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpm', label: 'CPM', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpc', label: 'CPC', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpv', label: 'CPV', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpw', label: 'CPW', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpf', label: 'CPF', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpo', label: 'CPO', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpl', label: 'CPL', format: 'currency', sortable: true, align: 'right' },
  { key: 'cplq', label: 'CPLQ', format: 'currency', sortable: true, align: 'right' },
  { key: 'cac', label: 'CAC', format: 'currency', sortable: true, align: 'right' },
]

const CAMPAIGN_COLUMNS = [
  { key: 'name', label: 'Campanha', sortable: true },
  { key: 'accountName', label: 'Conta', sortable: true },
  { key: 'status', label: 'Status', type: 'badge', sortable: true },
  { key: 'cost', label: 'Investimento', format: 'currency', sortable: true, align: 'right' },
  { key: 'revenue', label: 'Receita', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpm', label: 'CPM', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpc', label: 'CPC', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpv', label: 'CPV', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpw', label: 'CPW', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpf', label: 'CPF', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpo', label: 'CPO', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpl', label: 'CPL', format: 'currency', sortable: true, align: 'right' },
  { key: 'cplq', label: 'CPLQ', format: 'currency', sortable: true, align: 'right' },
  { key: 'cac', label: 'CAC', format: 'currency', sortable: true, align: 'right' },
]

const AD_GROUP_COLUMNS = [
  { key: 'name', label: 'Grupo de Anúncios', sortable: true },
  { key: 'campaignName', label: 'Campanha', sortable: true },
  { key: 'accountName', label: 'Conta', sortable: true },
  { key: 'status', label: 'Status', type: 'badge', sortable: true },
  { key: 'cost', label: 'Investimento', format: 'currency', sortable: true, align: 'right' },
  { key: 'revenue', label: 'Receita', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpm', label: 'CPM', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpc', label: 'CPC', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpv', label: 'CPV', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpw', label: 'CPW', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpf', label: 'CPF', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpo', label: 'CPO', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpl', label: 'CPL', format: 'currency', sortable: true, align: 'right' },
  { key: 'cplq', label: 'CPLQ', format: 'currency', sortable: true, align: 'right' },
  { key: 'cac', label: 'CAC', format: 'currency', sortable: true, align: 'right' },
]

const AD_COLUMNS = [
  { key: 'name', label: 'Anúncio', sortable: true },
  { key: 'groupName', label: 'Grupo', sortable: true },
  { key: 'campaignName', label: 'Campanha', sortable: true },
  { key: 'status', label: 'Status', type: 'badge', sortable: true },
  { key: 'cost', label: 'Investimento', format: 'currency', sortable: true, align: 'right' },
  { key: 'revenue', label: 'Receita', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpm', label: 'CPM', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpc', label: 'CPC', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpv', label: 'CPV', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpw', label: 'CPW', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpf', label: 'CPF', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpo', label: 'CPO', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpl', label: 'CPL', format: 'currency', sortable: true, align: 'right' },
  { key: 'cplq', label: 'CPLQ', format: 'currency', sortable: true, align: 'right' },
  { key: 'cac', label: 'CAC', format: 'currency', sortable: true, align: 'right' },
]

export default function ValuesPage({ data, level, onDrillDown }) {
  const dataWithCosts = useMemo(() => enrichWithCosts(data), [data])

  // Calcular custos médios ponderados globais (para os cards do topo)
  const averages = useMemo(() => {
    const totals = data.reduce((acc, item) => ({
      cost: acc.cost + (item.cost || 0),
      impressions: acc.impressions + (item.impressions || 0),
      clicks: acc.clicks + (item.clicks || 0),
      visits: acc.visits + (item.visits || 0),
      whatsappClicks: acc.whatsappClicks + (item.whatsappClicks || 0),
      forms: acc.forms + (item.forms || 0),
      contacts: acc.contacts + (item.contacts || 0),
      leads: acc.leads + (item.leads || 0),
      qualifiedLeads: acc.qualifiedLeads + (item.qualifiedLeads || 0),
      sales: acc.sales + (item.sales || 0),
      revenue: acc.revenue + (item.revenue || 0),
    }), {
      cost: 0, impressions: 0, clicks: 0, visits: 0, whatsappClicks: 0,
      forms: 0, contacts: 0, leads: 0, qualifiedLeads: 0, sales: 0, revenue: 0
    })

    return {
      revenue: totals.revenue,
      cpm: totals.impressions > 0 ? (totals.cost / totals.impressions) * 1000 : 0,
      cpc: totals.clicks > 0 ? totals.cost / totals.clicks : 0,
      cpv: totals.visits > 0 ? totals.cost / totals.visits : 0,
      cpw: totals.whatsappClicks > 0 ? totals.cost / totals.whatsappClicks : 0,
      cpf: totals.forms > 0 ? totals.cost / totals.forms : 0,
      cpo: totals.contacts > 0 ? totals.cost / totals.contacts : 0,
      cpl: totals.leads > 0 ? totals.cost / totals.leads : 0,
      cplq: totals.qualifiedLeads > 0 ? totals.cost / totals.qualifiedLeads : 0,
      cac: totals.sales > 0 ? totals.cost / totals.sales : 0,
    }
  }, [data])

  // Selecionar colunas baseado no nível de navegação
  const columns = useMemo(() => {
    switch (level) {
      case 'account': return ACCOUNT_COLUMNS
      case 'campaign': return CAMPAIGN_COLUMNS
      case 'adgroup': return AD_GROUP_COLUMNS
      case 'ad': return AD_COLUMNS
      default: return ACCOUNT_COLUMNS
    }
  }, [level])

  return (
    <div className="ga-page">
      {/* Cards de Custos Principais — mesmo visual de funil da Quantitativa */}
      <MetricFunnel
        rows={[[
          { label: 'Receita Total', value: averages.revenue, format: 'currency' },
          { label: 'CPM Médio', value: averages.cpm, format: 'currency' },
          { label: 'CPC Médio', value: averages.cpc, format: 'currency' },
          { label: 'CPV Médio', value: averages.cpv, format: 'currency' },
          { label: 'Custo por Whats', value: averages.cpw, format: 'currency' },
          { label: 'Custo por Form', value: averages.cpf, format: 'currency' },
          { label: 'Custo por Contato', value: averages.cpo, format: 'currency' },
          { label: 'CPL Médio', value: averages.cpl, format: 'currency' },
          { label: 'Custo por lead qualificado', value: averages.cplq, format: 'currency' },
          { label: 'CAC Médio', value: averages.cac, format: 'currency' },
        ]]}
      />

      {/* Tabela */}
      <div className="table-section">
        <div className="table-section-header">
          <div>
            <div className="table-section-title">
              Custos por Métrica
            </div>
            <div className="table-section-subtitle">
              Valores reais de custo de aquisição em cada etapa do funil • Clique no cabeçalho para ordenar
            </div>
          </div>
        </div>
        <DataTable
          columns={columns}
          data={dataWithCosts}
          hoverable
          striped
          scrollable
          onRowClick={onDrillDown}
          emptyText="Nenhum dado encontrado para os filtros selecionados"
        />
      </div>
    </div>
  )
}